import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useWidget } from "../../../providers/widget"
import { useLocaleDetection } from "../../../hooks/locale"
import { useLocalizedTranslation } from "../../../common/i18n"
import { useExplorer } from "../providers"
import { ExploreProperty, fetchQuote, QuoteRateplan, useFetchProperties, withBrowsePriceField } from "../data"
import { ExploreWidgetConfig } from "../config"
import PropertyCard from "./PropertyCard"
import PropertySkeleton from "./PropertySkeleton"
import WidgetDataError from "../../../components/WidgetDataError"
import { boundsFiltering } from "./map/initialView"
import { useCompareResults } from "../compare/useCompareResults"

type QuoteCache = Record<string, {
  rateplan: QuoteRateplan
  nights: number
} | 'loading' | 'failed'>

export default function PropertySlider() {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, mapActions } = useExplorer()
  // #1262: slider chrome strings — operator value → shipped locale table → en_US.
  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('explorer', options.translations, detectedLocale, options.language)
  const platformUuid = options.filter?.platform
  const [quoteCache, setQuoteCache] = useState<QuoteCache>({})
  const quoteQueueRef = useRef<ExploreProperty[]>([])
  const processingRef = useRef(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const sliderOptions = options.slider

  const boundsString = boundsFiltering(state) && state.mapBounds
    ? `${state.mapBounds.west},${state.mapBounds.south},${state.mapBounds.east},${state.mapBounds.north}`
    : undefined

  const filtersWithBounds = useMemo(() => {
    const filters = { ...state.visitorFilters }
    if (boundsString) {
      filters.map_bounds = boundsString
    }
    // Keep the key aligned with PropertyGrid + the SSR prefetch (shared cache entry).
    return withBrowsePriceField(filters, options)
  }, [state.visitorFilters, boundsString, options])

  const {
    data,
    isLoading,
    isError,
    error,
  } = useFetchProperties(feedId, filtersWithBounds, options.filter.limit, platformUuid)

  const properties = data?.pages.flatMap((page) => page.result) ?? []

  const isDateQuery = Boolean(state.visitorFilters.checkin && state.visitorFilters.checkout)
  const shouldFetchFullPrice = options.fullPrice && isDateQuery
  const showFavoriteButton = options.features?.favorites ?? false
  // Compare Homes (#1713): same contract as the grid; undefined per card unless compare mode is on.
  const compareFor = useCompareResults(properties, quoteCache, !isLoading)

  // Process full price queue sequentially
  const processQueue = useCallback(async () => {
    if (processingRef.current || quoteQueueRef.current.length === 0) return
    processingRef.current = true

    const property = quoteQueueRef.current.shift()!
    if (!property.slug) {
      processingRef.current = false
      processQueue()
      return
    }
    setQuoteCache(prev => ({ ...prev, [property.id]: 'loading' }))

    try {
      const result = await fetchQuote(
        feedId,
        {
          property: property.slug,
          checkin: state.visitorFilters.checkin!,
          checkout: state.visitorFilters.checkout!,
          guests: state.visitorFilters.guests ?? state.visitorFilters.adults ?? 1,
        },
              )

      if (result.success && result.result.rateplans?.[0]) {
        setQuoteCache(prev => ({
          ...prev,
          [property.id]: {
            rateplan: result.result.rateplans[0],
            nights: result.result.nights,
          },
        }))
      } else {
        setQuoteCache(prev => ({ ...prev, [property.id]: 'failed' }))
      }
    } catch {
      setQuoteCache(prev => ({ ...prev, [property.id]: 'failed' }))
    }

    processingRef.current = false
    processQueue()
  }, [feedId, state.visitorFilters])

  // Queue full price fetches when properties change
  useEffect(() => {
    if (!shouldFetchFullPrice || properties.length === 0) return

    setQuoteCache({})
    quoteQueueRef.current = [...properties]
    processingRef.current = false
    processQueue()
  }, [shouldFetchFullPrice, properties.length, processQueue])

  // Compute how many cards are visible at once
  const visibleCount = options.grid.columns960

  // Compute total "pages" (snap points) for dots
  const totalDots = Math.max(1, properties.length - visibleCount + 1)

  // Update scroll button visibility and active dot
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)

    // Calculate active index from scroll position
    if (properties.length > 0) {
      const cardWidthWithGap = el.scrollWidth / properties.length
      const newIndex = Math.round(el.scrollLeft / cardWidthWithGap)
      setActiveIndex(Math.min(newIndex, totalDots - 1))
    }
  }, [properties.length, totalDots])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [updateScrollState, properties.length])

  const scrollToIndex = useCallback((index: number) => {
    const el = scrollRef.current
    if (!el || properties.length === 0) return
    const cardWidthWithGap = el.scrollWidth / properties.length
    el.scrollTo({ left: cardWidthWithGap * index, behavior: 'smooth' })
  }, [properties.length])

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return

    if (sliderOptions.loop) {
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
      const atStart = el.scrollLeft <= 0

      if (direction === 'right' && atEnd) {
        el.scrollTo({ left: 0, behavior: 'smooth' })
        return
      }
      if (direction === 'left' && atStart) {
        el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' })
        return
      }
    }

    const scrollAmount = el.clientWidth * 0.8
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' })
  }, [sliderOptions.loop])

  // Autoplay
  useEffect(() => {
    if (!sliderOptions.autoplay || properties.length === 0) return

    autoplayRef.current = setInterval(() => {
      scroll('right')
    }, sliderOptions.autoplayInterval)

    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current)
    }
  }, [sliderOptions.autoplay, sliderOptions.autoplayInterval, scroll, properties.length])

  // Pause autoplay on hover
  const handleMouseEnter = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current)
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (!sliderOptions.autoplay || properties.length === 0) return
    autoplayRef.current = setInterval(() => {
      scroll('right')
    }, sliderOptions.autoplayInterval)
  }, [sliderOptions.autoplay, sliderOptions.autoplayInterval, scroll, properties.length])

  const gridVars = {
    '--hfse-grid-columns': options.grid.columns,
    '--hfse-grid-columns-780': options.grid.columns780,
    '--hfse-grid-columns-960': options.grid.columns960,
    '--hfse-grid-gap': `${options.grid.gap}px`,
  } as React.CSSProperties

  const showArrows = !sliderOptions.hideArrows
  const showDots = !sliderOptions.hideDots && totalDots > 1

  if (isLoading) {
    return (
      <div className="hfse-properties-container hfse-slider-container">
        <div className="hfse-slider-track" style={gridVars}>
          {Array.from({ length: options.grid.columns960 + 1 }, (_, i) => (
            <div key={i} className="hfse-slider-card">
              <PropertySkeleton index={i} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Show a data-fetch failure instead of the empty state (#740).
  if (isError) {
    return (
      <div className="hfse-properties-container">
        <WidgetDataError error={error} />
      </div>
    )
  }

  if (properties.length === 0) {
    return (
      <div className="hfse-properties-container">
        <div className="hfse-no-items">
          {options.noItemsText || _t('grid_no_results', 'No properties found.')}
        </div>
      </div>
    )
  }

  return (
    <div
      className="hfse-properties-container hfse-slider-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showArrows && canScrollLeft && (
        <button
          type="button"
          className="hfse-slider-nav hfse-slider-nav-prev"
          onClick={() => scroll('left')}
          aria-label={_t('slider_previous', 'Previous')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
      )}

      <div
        ref={scrollRef}
        className="hfse-slider-track"
        style={gridVars}
      >
        {properties.map((property) => {
          const cached = quoteCache[property.id]
          return (
            <div key={property.id} className="hfse-slider-card">
              <PropertyCard
                property={property}
                options={options}
                visitorFilters={state.visitorFilters}
                showFavoriteButton={showFavoriteButton}
                mapActions={state.mapVisible ? mapActions : null}
                quoteData={shouldFetchFullPrice && cached && cached !== 'loading' && cached !== 'failed' ? cached : undefined}
                quoteLoading={shouldFetchFullPrice && cached === 'loading'}
                quoteFailed={shouldFetchFullPrice && cached === 'failed'}
                compare={compareFor(property, shouldFetchFullPrice && cached && cached !== 'loading' && cached !== 'failed' ? cached : undefined)}
              />
            </div>
          )
        })}
      </div>

      {showArrows && canScrollRight && (
        <button
          type="button"
          className="hfse-slider-nav hfse-slider-nav-next"
          onClick={() => scroll('right')}
          aria-label={_t('slider_next', 'Next')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      )}

      {showDots && (
        <div className="hfse-slider-dots">
          {Array.from({ length: totalDots }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`hfse-slider-dot ${i === activeIndex ? 'active' : ''}`}
              onClick={() => scrollToIndex(i)}
              aria-label={_t('slider_go_to', 'Go to slide %1').replace('%1', String(i + 1))}
            />
          ))}
        </div>
      )}
    </div>
  )
}
